import { isBefore, isSameDay } from 'date-fns'

import type { HoldingDto } from '@hcr/api/consumer'
import { filter, flow, getPropertyValue, isNotEmpty, map } from '@hcr/utils'

import { compareDates, toDestinationDate } from '../../../dates'

const sortWeeksByDate = (weeks: HoldingDto['weeks']): HoldingDto['weeks'] => {
  const today = toDestinationDate(new Date())

  const futureWeeks = weeks.filter(week => {
    const weekStart = toDestinationDate(week.week_start)
    const weekEnd = toDestinationDate(week.week_end)

    return isSameDay(weekStart, today) || isBefore(today, weekStart) || isBefore(today, weekEnd)
  })
  const pastWeeks = weeks.filter(week => {
    const weekStart = toDestinationDate(week.week_start)
    const weekEnd = toDestinationDate(week.week_end)

    return isBefore(weekStart, today) && isBefore(weekEnd, today)
  })

  return [
    ...futureWeeks.sort((a, b) => compareDates(a.week_start, b.week_start)),
    ...pastWeeks.sort((a, b) => compareDates(a.week_start, b.week_start)),
  ]
}

const sortHoldingsByWeekStart = (holdings: HoldingDto[]): HoldingDto[] => {
  const today = toDestinationDate(new Date())

  return holdings.sort((a, b) => {
    if (!a.weeks.length || !b.weeks.length) return 0

    const aDate = toDestinationDate(a.weeks[0].week_start)
    const bDate = toDestinationDate(b.weeks[0].week_start)

    const isAPast = a.weeks.every(week => isBefore(toDestinationDate(week.week_end), today))
    const isBPast = b.weeks.every(week => isBefore(toDestinationDate(week.week_end), today))

    if (isAPast && !isBPast) return 1
    if (!isAPast && isBPast) return -1

    return compareDates(aDate, bDate)
  })
}

export const selectHoldingsWithNextWeeks: (holdings: HoldingDto[]) => HoldingDto[] = flow(
  map(holding => ({
    ...holding,
    weeks: sortWeeksByDate(holding.weeks),
  })),
  filter(flow(getPropertyValue('weeks'), isNotEmpty)),
  sortHoldingsByWeekStart
)
